<template>
    <div>
        <base-dialog :loading="loading" :value="dialog" :width="920" :fullscreen="isMobile" :title="$t('common.load_data')" @input="onInput" @close="hide">
            <div class="lg-controls">
                <div class="lg-control">
                    <label class="lg-control-label">{{ $t('common.date') }}</label>
                    <kimsa-date-picker solo flat dense color="secondary" :value="date" centered-value elevation
                        @change="onChangeDate" :events="datesLoaded" />
                </div>
                <div class="lg-control">
                    <label class="lg-control-label">{{ $t('common.subgroup') }}</label>
                    <kimsa-combobox :placeholder="$t('common.select_subgroup')" dense :items="groups"
                        @change="group = $event" :value="group" />
                </div>
            </div>

            <skeleton-loader v-if="loading" type="rows" />
            <template v-else>
                <template v-if="studentsTeam.length > 0">
                    <div class="lg-search">
                        <kimsa-text-field dense :placeholder="$t('common.search')" prepend-inner-icon="mdi-magnify"
                            @change="search = $event" :value="search" />
                    </div>
                    <div class="lg-table-scroll">
                        <table class="lg-table">
                            <thead>
                                <tr>
                                    <th class="lg-th-name">{{ $t('common.name') }}</th>
                                    <th v-for="h in orderedConfig" :key="h.id" class="lg-th-metric"
                                        @click="onClickHeader(h)">
                                        {{ h.name }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="student in paginatedStudents" :key="student.id">
                                    <td class="lg-td-name">{{ student.name }}</td>
                                    <td v-for="h in orderedConfig" :key="h.id">
                                        <kimsa-text-field dense centered-value :placeholder="$t(`units.${h.unit}`)"
                                            :value="payload[student.id] ? payload[student.id][h.id] : null"
                                            @change="onChangeValue(student.id, h.id, $event)" force-value />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-if="pageCount > 1" class="lg-pagination">
                        <span class="lg-pagination-info">
                            {{ $tc('common.students_count', filteredStudents.length, { n: filteredStudents.length }) }}
                        </span>
                        <kimsa-pagination :value="page" @page="page = $event" :pageCount="pageCount"
                            :total-visible="isMobile ? 4 : 7" height="auto" no-right />
                    </div>
                </template>
                <div v-else class="lg-empty">{{ $t('common.without_student_subgroup_added') }}</div>
            </template>

            <template #footer>
                <dialog-actions :confirm-label="$t('common.save')" :disabled="disabled" :loading="loadingSave"
                    @cancel="hide" @confirm="assign" />
            </template>
        </base-dialog>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { db } from "@/utils/Firebase";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaCombobox from '../common/inputs/KimsaCombobox.vue';
import KimsaDatePicker from '../common/inputs/KimsaDatePicker.vue';
import BaseDialog from '@/components/common/BaseDialog';
import DialogActions from '@/components/common/DialogActions';
import SkeletonLoader from '@/components/common/SkeletonLoader.vue';
import KimsaPagination from '@/components/common/KimsaPagination.vue';

export default {
    name: "LoadDataEvaluationGroupDialog",
    components: { KimsaTextField, KimsaCombobox, KimsaDatePicker, BaseDialog, DialogActions, SkeletonLoader, KimsaPagination },
    props: {
        students: {
            type: Array,
            default: () => []
        },
        coachId: {
            type: String,
            default: null
        },
    },
    data() {
        return {
            dialog: false,
            loading: false,
            loadingSave: false,
            team: null,
            evaluationId: null,
            assignedEvaluationId: null,
            payload: {},
            hasLoadedToday: false,
            group: null,
            search: '',
            firstHeader: null,
            currentAE: null,
            date: this.$moment().format('YYYY-MM-DD'),
            page: 1,
            perPage: 8,
        }
    },
    computed: {
        ...mapState(['evaluations']),
        disabled(vm = this) {
            return vm.loading || !vm.hasValues
        },
        hasValues(vm = this) {
            return vm.teamStudentIds.some(id => {
                const studentPayload = vm.payload?.[id]
                if (!studentPayload) return false
                return vm.config.some(c => {
                    const v = studentPayload[c.id]
                    return v !== null && v !== undefined && v !== '' && !isNaN(v)
                })
            })
        },
        currentEvaluation(vm = this) {
            if (!vm.evaluationId) return null
            return vm.evaluations.find(eva => eva.id === vm.evaluationId) || null
        },
        config(vm = this) {
            if (!vm.currentEvaluation) return []
            return vm.currentEvaluation.config || []
        },
        orderedConfig(vm = this) {
            if (!vm.firstHeader) return vm.config
            return [...vm.config].sort((a, b) => {
                if (a.id === vm.firstHeader.id) return -1
                if (b.id === vm.firstHeader.id) return 1
                return 0
            })
        },
        groups(vm = this) {
            return vm.team?.evaluationGroups || []
        },
        studentsTeam(vm = this) {
            return vm.students.filter(student => vm.group?.students?.includes(student.id))
        },
        filteredStudents(vm = this) {
            if (!vm.search) return vm.studentsTeam
            const query = vm.search.toLowerCase()
            return vm.studentsTeam.filter(student => (student.name || '').toLowerCase().includes(query))
        },
        pageCount(vm = this) {
            return Math.ceil(vm.filteredStudents.length / vm.perPage) || 1
        },
        paginatedStudents(vm = this) {
            const start = (vm.page - 1) * vm.perPage
            return vm.filteredStudents.slice(start, start + vm.perPage)
        },
        teamStudentIds(vm = this) {
            const ids = new Set()
            const groups = vm.team?.evaluationGroups || []
            groups.forEach(group => (group.students || []).forEach(id => ids.add(id)))
            return [...ids]
        },
        formattedDate(vm = this) {
            return vm.$moment(vm.date).format('DD-MM-YYYY')
        },
        datesLoaded(vm = this) {
            return vm.currentAE?.values?.map(v => vm.$moment(v.date, 'DD-MM-YYYY').format('YYYY-MM-DD')) || []
        }
    },
    async mounted() {
        const vm = this
        vm.loadEvaluations()
    },
    methods: {
        ...mapActions(['bindEvaluations']),
        show(team, assignedEvaluationId) {
            const vm = this
            vm.team = team
            vm.group = team?.evaluationGroups?.[0] || null
            vm.assignedEvaluationId = assignedEvaluationId
            vm.dialog = true
            vm.loadAssignedEvaluation(assignedEvaluationId)
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        onInput(val) {
            if (!val) this.hide()
        },
        reset() {
            const vm = this
            vm.team = null
            vm.group = null
            vm.evaluationId = null
            vm.assignedEvaluationId = null
            vm.hasLoadedToday = false
            vm.firstHeader = null
            vm.currentAE = null
            vm.search = ''
            vm.date = vm.$moment().format('YYYY-MM-DD')
        },
        async loadEvaluations() {
            const vm = this;
            if (vm._checkNeedReloadBind(vm.evaluations, vm.coachId)) await vm.bindEvaluations(vm.coachId)
        },
        loadPayloadStructure(values) {
            const vm = this
            if (!vm.currentEvaluation || vm.teamStudentIds.length === 0) return;

            vm.payload = { ...(values || {}), date: vm.formattedDate }
            vm.teamStudentIds.forEach(studentId => {
                const existing = vm.payload[studentId] || {}
                let payloadConfig = {}
                vm.config.forEach(con => {
                    payloadConfig[con.id] = existing[con.id] ?? null
                })
                vm.$set(vm.payload, studentId, payloadConfig)
            })
        },
        async loadAssignedEvaluation(assignedEvaluationId) {
            const vm = this
            await vm.withLoading(async () => {
                let ref = await db.collection('assignedEvaluations').doc(assignedEvaluationId).get()
                vm.currentAE = ref.data()
                vm.evaluationId = vm.currentAE.evaluationId || null
                let values = vm.currentAE?.values?.find(value => value.date === vm.formattedDate)
                vm.loadPayloadStructure(values)
            })
        },
        onChangeDate(date) {
            const vm = this;
            if (!vm.dialog) return;
            vm.date = date
            let values = vm.currentAE?.values?.find(value => value.date === vm.formattedDate)
            vm.loadPayloadStructure(values)
        },
        onChangeValue(studentId, configId, value) {
            const vm = this
            if (!vm.payload[studentId]) vm.$set(vm.payload, studentId, {})
            vm.payload[studentId][configId] = value
        },
        async assign() {
            const vm = this
            await vm.withLoading(async () => {
                let payloadValues = { values: vm.currentAE.values || [] }
                const index = vm.currentAE?.values?.findIndex(value => value.date === vm.formattedDate)
                if (index !== -1) {
                    payloadValues.values[index] = vm.payload
                } else {
                    payloadValues.values.push(vm.payload)
                }

                payloadValues.values.sort((a, b) => vm.parseDateString(a.date) - vm.parseDateString(b.date))
                await db.collection('assignedEvaluations').doc(vm.assignedEvaluationId).update(payloadValues)

                vm.toast(vm.$t('common.loaded_data'))
                vm.hide()
            }, 'loadingSave')
        },
        onClickHeader(header) {
            const vm = this
            vm.firstHeader = header
        },
        parseDateString(dateString) {
            const [day, month, year] = dateString.split('-').map(Number)
            return new Date(year, month - 1, day)
        }
    },
    watch: {
        coachId() {
            this.loadEvaluations()
        },
        search() {
            this.page = 1
        },
        group() {
            this.page = 1
        },
        filteredStudents() {
            if (this.page > this.pageCount) this.page = 1
        },
    }
}
</script>

<style scoped>
.lg-controls {
    display: flex;
    gap: 16px;
    margin-bottom: 14px;
}

.lg-control {
    flex: 1;
    min-width: 0;
}

.lg-control-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
    margin-bottom: 6px;
}

.lg-search {
    margin-bottom: 12px;
}

.lg-table-scroll {
    overflow-x: auto;
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
}

.lg-table {
    width: 100%;
    border-collapse: collapse;
}

.lg-table thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 10px 14px;
    text-align: center;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--v-txtMuted-base);
    background: var(--v-surface2-base);
    white-space: nowrap;
}

.lg-th-metric {
    cursor: pointer;
    user-select: none;
    min-width: 128px;
}

.lg-th-metric:hover {
    color: var(--v-accent-base);
}

.lg-table tbody tr {
    border-top: 1px solid var(--v-outline-base);
}

.lg-table td {
    padding: 6px 14px;
    vertical-align: middle;
}

.lg-table tbody td:not(.lg-td-name) {
    min-width: 128px;
}

/* Sticky first column (student name) */
.lg-th-name,
.lg-td-name {
    position: sticky;
    left: 0;
    white-space: nowrap;
}

.lg-table thead th.lg-th-name {
    z-index: 3;
    text-align: left;
    padding-right: 24px;
    background: var(--v-surface2-base);
    box-shadow: 6px 0 8px -6px rgba(0, 0, 0, 0.25);
}

.lg-table tbody td.lg-td-name {
    z-index: 2;
    padding-right: 24px;
    background: var(--v-surface-base);
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtPrimary-base);
    box-shadow: 6px 0 8px -6px rgba(0, 0, 0, 0.25);
}

.lg-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 12px;
}

.lg-pagination-info {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    white-space: nowrap;
}

.lg-empty {
    text-align: center;
    font-size: 13px;
    color: var(--v-txtMuted-base);
    padding: 32px 0;
}
</style>
